import { useState } from 'react';
import {
  getSupabaseConfigError,
  isSupabaseConfigured,
  supabase,
} from '../lib/supabase';

function normalizePhone(value) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

function ReferralLookup() {
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const onLookup = async (event) => {
    event.preventDefault();
    setCopied(false);
    setResult(null);

    const digits = normalizePhone(phone);
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setStatus({ type: 'error', message: 'Please enter the 10-digit mobile number you used to join.' });
      return;
    }

    const configError = getSupabaseConfigError();
    if (configError || !isSupabaseConfigured || !supabase) {
      setStatus({ type: 'error', message: configError });
      return;
    }

    setLoading(true);
    setStatus({ type: '', message: '' });

    const { data, error } = await supabase.rpc('lookup_driver_referrals', {
      phone_input: digits,
    });

    setLoading(false);

    if (error) {
      setStatus({
        type: 'error',
        message:
          error.message?.toLowerCase().includes('lookup_driver_referrals')
            ? 'Referral lookup is not set up yet. Run supabase/add-license-back-referral-lookup.sql in the SQL Editor.'
            : error.message || 'Could not check referrals. Please try again.',
      });
      return;
    }

    if (!data?.found) {
      setStatus({
        type: 'error',
        message: 'We could not find this mobile number. Please check and try again.',
      });
      return;
    }

    setResult(data);
  };

  const copyCode = async () => {
    if (!result?.referral_code) return;
    try {
      await navigator.clipboard.writeText(result.referral_code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <form className="referral-box" onSubmit={onLookup} noValidate>
      <div className="wizard-panel__head">
        <h3>See your code</h3>
        <p>Type the same mobile number you used when you joined Sarthi.</p>
      </div>

      <label className="field">
        <span>Your mobile number</span>
        <div className="phone-field">
          <span aria-hidden="true">+91</span>
          <input
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              setStatus({ type: '', message: '' });
            }}
            inputMode="numeric"
            autoComplete="tel"
            maxLength={13}
            placeholder="10-digit mobile"
          />
        </div>
      </label>

      {status.message && (
        <div className={`form-alert form-alert--${status.type}`} role="status">
          {status.message}
        </div>
      )}

      {result && (
        <div className="referral-result">
          <p>
            Namaste, <strong>{result.full_name || 'Driver'}</strong>
          </p>
          <div className="referral-result__code">
            <span>Share this code with friends</span>
            <strong>{result.referral_code || 'Not ready yet'}</strong>
          </div>
          <div className="referral-result__count">
            <strong>{result.referral_count ?? 0}</strong>
            <span>
              {(result.referral_count ?? 0) === 1
                ? 'friend joined with your code'
                : 'friends joined with your code'}
            </span>
          </div>
          {result.referral_code && (
            <button type="button" className="btn btn--ghost-dark" onClick={copyCode}>
              {copied ? 'Code copied' : 'Copy my code'}
            </button>
          )}
        </div>
      )}

      <div className="wizard-actions">
        <span />
        <button type="submit" className="btn btn--primary" disabled={loading}>
          {loading ? 'Please wait…' : 'Show my code'}
        </button>
      </div>
    </form>
  );
}

export default ReferralLookup;
